import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import ErrorToast, {
  showToast,
} from '../../../../components/messages/ErrorAlert';
import {
  Column,
  ColumnEditorOptions,
  ColumnEventParams,
} from 'primereact/column';
import Table from '../../../../components/Table/Table';
import { formatCurrency, formatDate } from '../../../../../utils/formatUtils';
import { DataTableRowMouseEventParams } from 'primereact/datatable';
import { animateCellText } from '../../../../../utils/htmlUtils';
import {
  JCProjectedCost,
  CostDetailArgs,
  DraftCosts,
  JCProjectedCostDraft,
} from '../../../../Interfaces/Accounting/JobsContracts.interface';
import { InputText } from 'primereact/inputtext';
import JCCostDrilldownContainer from '../JCCostDrilldown/JCCostDrilldownContainer';
import JCCommittedCostContainer from '../JCCommittedCostDrilldown/JCCommittedCostContainer';
import JCProjectedCostProcess from './JCProjectedCostProcess';
import { useCompanyContext } from '../../../../context/CompanyContext';
import WrapperButton from '../../../../components/buttons/WrapperButton';
import TooltipContent from '../../../../components/messages/TooltipContent';
import JCNoteButton from './JCNoteButton';
import { Access } from '../../../../Interfaces/admin/Role.interfaces';
import ApprovalHistory from '../../../../components/approvals/ApprovalHistory';
import { getProjectedCostBase, getProjectedCostKey } from './utils';
import { useQuery } from '@tanstack/react-query';
import { fecthJCPhaseMaster } from '../../../../services/accounting/JobsContractsService';
import ProjectedCostTypeCell from './cells/ProjectedCostTypeCell';
import ProjectedPhaseCell from './cells/ProjectedPhaseCell';
import ProjectedDescriptionCell from './cells/ProjectedDescriptionCell';

type JCProjectedCostTableProps = {
  costs: JCProjectedCost[];
  draftDefault?: JCProjectedCostDraft;
  approval?: boolean;
  access: Access;
  months?: string[];
  hidePhaseDetails?: string[];
  afterApproval?: (draftID: string) => void;
};

const JCProjectedCostTable = React.forwardRef<
  HTMLDivElement,
  JCProjectedCostTableProps
>(
  (
    {
      costs,
      draftDefault,
      approval,
      access,
      months,
      hidePhaseDetails,
      afterApproval,
    },
    ref
  ) => {
    const { selectedCompany } = useCompanyContext();
    const canEdit =
      (!!access.editable || (access.shouldApprove && approval)) &&
      !!selectedCompany?.isActive;
    const tableClass = 'mx-3 dashboardOptionShadow hoverEffect';
    const columnHeaderClass = 'tableHeader';
    const [tableCosts, setTableCosts] = useState(costs);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedCost, setSelectedCost] = useState<CostDetailArgs>();
    const [showDrilldown, setShowDrilldown] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [drilldownType, setDrilldownType] = useState('');
    const [costsDraft, setCostsDraft] = useState<DraftCosts>(
      draftDefault ? { ...draftDefault.costs } : {}
    );
    const [showErrors, setShowErrors] = useState(false);
    const toast = useRef<Toast>(null);

    const phaseMasterRequest = useQuery({
      queryKey: ['getProjectedPhaseMaster', selectedCompany],
      queryFn: ({ signal }) =>
        fecthJCPhaseMaster(selectedCompany!.id, undefined, signal),
      refetchOnWindowFocus: false,
      enabled: !!selectedCompany && canEdit,
    });

    const calcHeight = (rows: number) => {
      const headerHeight = 32;
      const footerHeight = 54;
      const rowHeight = 31;
      return headerHeight + footerHeight + rows * rowHeight + 3;
    };

    const onRowHover = (e: DataTableRowMouseEventParams) => {
      const cell = e.originalEvent.target as HTMLElement;
      const row = cell.closest('tr') as HTMLElement;
      animateCellText(row);
    };

    const extraColumns = useMemo(() => {
      return months?.map((month) => {
        return (
          <Column
            key={month}
            field={`${month}.amount`}
            header={month}
            body={(jc) =>
              jc.isAddRow ? null : jc[month]?.amount ? (
                jc[month]?.note ? (
                  <TooltipContent
                    id={`${month.replace(/\//g, '_')}-${jc.Phase.replace(
                      / /g,
                      '_'
                    )}`}
                    label={formatCurrency(jc[month].amount) || ''}
                    content={jc[month].note}
                    bold={jc[month].note}
                  />
                ) : (
                  formatCurrency(jc[month]?.amount)
                )
              ) : (
                ''
              )
            }
            headerClassName={`font-normal ${columnHeaderClass} justify-content-center bg-teal-300`}
            style={{ minWidth: '130px' }}
            className={`text-standard blackText tableCell justify-content-end`}
            footer={(e) => {
              const records = e.props.value;
              const total = useMemo(() => {
                let totalValue = 0;
                records.forEach(
                  (
                    record: Record<string, { amount: number; note?: string }>
                  ) => {
                    totalValue += record[month]?.amount || 0;
                  }
                );
                return formatCurrency(totalValue);
              }, [records, month]);

              return total;
            }}
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            sortable
          />
        );
      });
    }, [months]);

    const textEditor = (options: ColumnEditorOptions) => {
      const editorCallback = options.editorCallback;

      return (
        <div className="p-2 w-full">
          <InputText
            type="text"
            keyfilter="num"
            className="w-full"
            value={options.value || ''}
            onChange={(e) => {
              try {
                if (editorCallback) {
                  editorCallback(e.target.value);
                }
              } catch (e) {
                console.log(e);
              }
            }}
            onFocus={() => setIsEditing(true)}
          />
        </div>
      );
    };

    const onCellEditComplete = (e: ColumnEventParams) => {
      const { rowData, newValue, field } = e;

      if (rowData.isAddRow) {
        setIsEditing(false);
        return;
      }

      const number = parseFloat(newValue || '');
      const rounded = isNaN(number) ? null : Math.round(number * 100) / 100;

      rowData[field] = rounded;
      rowData.OverUnder = rounded
        ? Math.round((getProjectedCostBase(rowData) - rounded) * 100) / 100
        : null;

      if (rowData.isNew) {
        if (rowData.CostType) {
          const newKey = getProjectedCostKey(rowData.Phase, rowData.CostType);
          setCostsDraft((values) => ({
            ...values,
            [newKey]: {
              ...(values[newKey] ?? {}),
              isNew: true,
              description: rowData.Description,
              amount: rounded ?? undefined,
            },
          }));
        }
        setTableCosts((costs) => [...costs]);
        setIsEditing(false);
        return;
      }

      const key = getProjectedCostKey(rowData.Phase, rowData.CostType);

      setCostsDraft((values) => {
        const draft = values[key] ?? {};

        if (rounded) {
          values[key] = { ...draft, amount: rounded };
          return { ...values };
        } else if (draft.amount) {
          values[key].amount = undefined;
          if (!draft.note) {
            delete values[key];
          }
          return { ...values };
        }

        return values;
      });

      setIsEditing(false);
    };

    const onNoteEditComplete = (
      phase: string,
      costType: number,
      newValue?: string
    ) => {
      const key = getProjectedCostKey(phase, costType);

      const newRow = tableCosts.find(
        (cost) =>
          cost.isNew && cost.Phase === phase && cost.CostType === costType
      );

      if (newRow) {
        setCostsDraft((values) => ({
          ...values,
          [key]: {
            ...(values[key] ?? {}),
            isNew: true,
            description: newRow.Description,
            note: newValue || undefined,
          },
        }));
        return;
      }

      setCostsDraft((values) => {
        const draft = values[key] ?? {};

        if (newValue) {
          values[key] = { ...draft, note: newValue };
          return { ...values };
        } else if (draft.note) {
          values[key].note = undefined;
          if (!draft.amount) {
            delete values[key];
          }
          return { ...values };
        }

        return values;
      });

      setTableCosts((costs) => {
        const newCosts = costs.map((cost) => cost);
        return newCosts;
      });
    };

    const onCellClick = (cost: JCProjectedCost, drilldownType: string) => {
      if (hidePhaseDetails?.includes(cost.Phase)) {
        return;
      }

      setSelectedCost({
        job: cost.Job,
        jobName: cost.JobName,
        phase: cost.Phase,
        phaseDescription: cost.Description,
        costType: cost.CostType,
      });
      setShowDrilldown(true);
      setDrilldownType(drilldownType);
    };

    const hideDrilldown = () => {
      setSelectedCost(undefined);
      setShowDrilldown(false);
    };

    const reset = () => {
      setCostsDraft({});
      setTableCosts((costs) => {
        const newCosts = costs
          .filter((cost) => !cost.isNew)
          .map((cost) => {
            if (cost.NewProjCost != null) {
              delete cost.NewProjCost;
              delete cost.OverUnder;
            }

            return cost;
          });

        return newCosts;
      });
    };

    const addRow = () => {
      setTableCosts((costs) => [
        ...costs,
        {
          Job: costs[0]?.Job ?? '',
          JobName: costs[0]?.JobName ?? '',
          Phase: '',
          Description: '',
          CostType: 0,
          CostTypeDescription: '',
          OriginalEst: 0,
          CurrentEst: 0,
          ActualCost: 0,
          CommitCost: 0,
          RemCommit: 0,
          ProjCost: 0,
          isNew: true,
        },
      ]);
    };

    const removeRow = (row: JCProjectedCost) => {
      if (row.Phase && row.CostType) {
        const key = getProjectedCostKey(row.Phase, row.CostType);
        setCostsDraft((values) => {
          const next = { ...values };
          delete next[key];
          return next;
        });
      }
      setTableCosts((costs) => costs.filter((cost) => cost !== row));
    };

    const onPhaseChange = (
      row: JCProjectedCost,
      phase?: string,
      description?: string
    ) => {
      const oldKey =
        row.Phase && row.CostType
          ? getProjectedCostKey(row.Phase, row.CostType)
          : null;

      row.Phase = phase ?? '';
      row.Description = description ?? '';
      row.CostType = 0;
      row.CostTypeDescription = '';
      delete row.NewProjCost;
      delete row.OverUnder;

      if (oldKey) {
        setCostsDraft((values) => {
          const next = { ...values };
          delete next[oldKey];
          return next;
        });
      }

      setTableCosts((costs) => [...costs]);
    };

    const onCostTypeChange = (row: JCProjectedCost, costType?: number) => {
      const oldKey =
        row.Phase && row.CostType
          ? getProjectedCostKey(row.Phase, row.CostType)
          : null;

      row.CostType = costType ?? 0;

      const newKey =
        row.Phase && row.CostType
          ? getProjectedCostKey(row.Phase, row.CostType)
          : null;

      setCostsDraft((values) => {
        const next = { ...values };
        const previous = oldKey ? next[oldKey] : undefined;

        if (oldKey && oldKey !== newKey) {
          delete next[oldKey];
        }

        if (newKey) {
          next[newKey] = {
            ...(previous ?? {}),
            isNew: true,
            description: row.Description,
          };
        }

        return next;
      });

      setTableCosts((costs) => [...costs]);
    };

    const onDescriptionChange = (
      row: JCProjectedCost,
      description?: string
    ) => {
      row.Description = description ?? '';

      if (row.Phase && row.CostType) {
        const key = getProjectedCostKey(row.Phase, row.CostType);
        setCostsDraft((values) =>
          values[key]
            ? {
                ...values,
                [key]: { ...values[key], description: description ?? '' },
              }
            : values
        );
      }

      setTableCosts((costs) => [...costs]);
    };

    const validateNewRows = () => {
      const hasIncompleteRows = tableCosts.some(
        (cost) =>
          cost.isNew && (!cost.Phase || !cost.CostType || !cost.Description)
      );

      if (hasIncompleteRows) {
        setShowErrors(true);
        showToast(
          'error',
          toast,
          'Projected Costs',
          'Please complete or remove the new rows. Phase, Cost Type and Description are required.',
          4000
        );
        return false;
      }

      setShowErrors(false);
      return true;
    };

    const usedPhaseCostTypeKeys = useMemo(
      () =>
        new Set(
          tableCosts
            .filter((cost) => cost.Phase && cost.CostType)
            .map((cost) => getProjectedCostKey(cost.Phase, cost.CostType))
        ),
      [tableCosts]
    );

    let totOrig = 0;
    let totEst = 0;
    let totActual = 0;
    let totCommitt = 0;
    let totRemCommitt = 0;
    let totProjCost = 0;
    let totProjBase = 0;
    let overUnder = 0;

    tableCosts.forEach((cost) => {
      totOrig += cost.OriginalEst;
      totEst += cost.CurrentEst;
      totActual += cost.ActualCost;
      totCommitt += cost.CommitCost;
      totRemCommitt += cost.RemCommit;
      totProjCost += cost.ProjCost;
      totProjBase += getProjectedCostBase(cost);
      overUnder += cost.OverUnder || 0;
    });

    return (
      <div className="pdfDivFitContent">
        <ErrorToast toastRef={toast} />
        {selectedCost && drilldownType === 'actual' && (
          <JCCostDrilldownContainer
            visible={showDrilldown}
            hideDrilldown={hideDrilldown}
            costDetail={{ ...selectedCost }}
          />
        )}
        {selectedCost && drilldownType === 'committed' && (
          <JCCommittedCostContainer
            visible={showDrilldown}
            hideDrilldown={hideDrilldown}
            costDetail={{ ...selectedCost }}
          />
        )}
        <Table
          id="jc-projected-cost"
          data={
            canEdit
              ? [
                  ...tableCosts,
                  { isAddRow: true } as unknown as JCProjectedCost,
                ]
              : tableCosts
          }
          className={tableClass}
          calcHeight={useCallback(calcHeight, [])}
          onRowMouseEnter={useCallback(onRowHover, [])}
          headerContent={
            draftDefault?.approvals ? (
              <React.Fragment>
                <div
                  className="text-18px font-bold cursor-pointer totalColor hover:underline flex"
                  onClick={() => setShowHistory(true)}
                >
                  Check Draft Approvals
                </div>
                <ApprovalHistory
                  show={showHistory}
                  hide={() => {
                    setShowHistory(false);
                  }}
                  approvals={draftDefault.approvals}
                  lastStep={draftDefault.lastStep ?? 0}
                />
              </React.Fragment>
            ) : undefined
          }
        >
          <Column
            field="Phase"
            header="Phase"
            body={(jc) => {
              if (jc.isAddRow) {
                return (
                  <React.Fragment>
                    <i
                      id="add-projected-phase"
                      className="pi pi-plus cursor-pointer text-green-500"
                      onClick={addRow}
                      data-pr-position="right"
                      data-pr-tooltip="Add new phase"
                    />
                    <Tooltip target="#add-projected-phase" />
                  </React.Fragment>
                );
              }

              if (jc.isNew) {
                return (
                  <ProjectedPhaseCell
                    row={jc}
                    options={phaseMasterRequest.data}
                    isLoading={phaseMasterRequest.isFetching}
                    isError={phaseMasterRequest.isError}
                    onChange={(phase, description) =>
                      onPhaseChange(jc, phase, description)
                    }
                    showError={showErrors}
                  />
                );
              }

              return jc.Phase;
            }}
            style={{ minWidth: '120px', maxWidth: '120px' }}
            headerClassName="tableHeader font-normal text-center"
            className="justify-content-center text-standard blackText tableCell"
            footerClassName="tableFooter"
            sortable
          />
          <Column
            field="Description"
            header="Description"
            body={(jc) => {
              if (jc.isAddRow) {
                return null;
              }

              if (jc.isNew) {
                if (!jc.Phase) {
                  return null;
                }

                return (
                  <ProjectedDescriptionCell
                    row={jc}
                    onChange={(description) =>
                      onDescriptionChange(jc, description)
                    }
                    showError={showErrors}
                  />
                );
              }

              return <div className="scroll-text">{jc.Description}</div>;
            }}
            style={{ minWidth: '250px', maxWidth: '250px' }}
            headerClassName="tableHeader font-normal justify-content-center"
            className="text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow"
            footerClassName="tableFooter"
            sortable
          />
          <Column
            field="CostTypeDescription"
            header="Cost Type"
            body={(jc) => {
              if (jc.isAddRow) {
                return null;
              }

              if (jc.isNew) {
                if (!jc.Phase) {
                  return null;
                }

                return (
                  <ProjectedCostTypeCell
                    key={jc.Phase}
                    row={jc}
                    usedKeys={usedPhaseCostTypeKeys}
                    onChange={(costType) => onCostTypeChange(jc, costType)}
                    showError={showErrors}
                  />
                );
              }

              return (
                <div className="scroll-text">{jc.CostTypeDescription}</div>
              );
            }}
            style={{ minWidth: '150px', maxWidth: '150px' }}
            headerClassName="tableHeader font-normal justify-content-center"
            className="text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow"
            footerClassName="tableFooter block text-right border-top-2 border-transparent mt-4 limitBorder relative"
            footer="Total"
            sortable
          />
          <Column
            field="OriginalEst"
            header="Original Estimate"
            body={(jc) => (jc.isAddRow ? null : formatCurrency(jc.OriginalEst))}
            headerClassName={`font-normal ${columnHeaderClass} justify-content-center`}
            style={{ minWidth: '160px' }}
            className={`text-standard blackText tableCell justify-content-end`}
            footer={formatCurrency(totOrig)}
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            sortable
          />
          <Column
            field="CurrentEst"
            header="Current Estimate"
            body={(jc) => (jc.isAddRow ? null : formatCurrency(jc.CurrentEst))}
            headerClassName={`font-normal ${columnHeaderClass} justify-content-center`}
            style={{ minWidth: '160px' }}
            className={`text-standard blackText tableCell justify-content-end`}
            footer={formatCurrency(totEst)}
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            sortable
          />
          <Column
            field="ActualCost"
            header="Actual Cost"
            body={(jc) =>
              jc.isAddRow ? null : jc.isNew ? (
                formatCurrency(jc.ActualCost)
              ) : (
                <WrapperButton
                  disabled={hidePhaseDetails?.includes(jc.Phase)}
                  className={`w-full text-right printColor ${
                    !hidePhaseDetails?.includes(jc.Phase)
                      ? 'totalColor'
                      : 'blackText'
                  }`}
                  onClick={() => onCellClick(jc, 'actual')}
                >
                  {formatCurrency(jc.ActualCost)}
                </WrapperButton>
              )
            }
            headerClassName={`font-normal ${columnHeaderClass} justify-content-center`}
            style={{ minWidth: '150px' }}
            className={`text-standard tableCell justify-content-end`}
            footer={formatCurrency(totActual)}
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            sortable
          />
          <Column
            field="CommitCost"
            header="Committed Costs"
            body={(jc) =>
              jc.isAddRow ? null : jc.isNew ? (
                formatCurrency(jc.CommitCost)
              ) : (
                <WrapperButton
                  disabled={hidePhaseDetails?.includes(jc.Phase)}
                  className={`w-full text-right printColor ${
                    !hidePhaseDetails?.includes(jc.Phase)
                      ? 'totalColor'
                      : 'blackText'
                  }`}
                  onClick={() => onCellClick(jc, 'committed')}
                >
                  {formatCurrency(jc.CommitCost)}
                </WrapperButton>
              )
            }
            headerClassName={`font-normal ${columnHeaderClass} justify-content-center`}
            style={{ minWidth: '160px' }}
            className={`text-standard tableCell justify-content-end`}
            footer={formatCurrency(totCommitt)}
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            sortable
          />
          <Column
            field="RemCommit"
            header="Remaining to Commit"
            body={(jc) => (jc.isAddRow ? null : formatCurrency(jc.RemCommit))}
            headerClassName={`font-normal ${columnHeaderClass} justify-content-center`}
            style={{ minWidth: '190px' }}
            className={`text-standard blackText tableCell justify-content-end`}
            footer={formatCurrency(totRemCommitt)}
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            sortable
          />
          {extraColumns}
          <Column
            field="ProjCost"
            header={
              (extraColumns?.length || 0) > 0
                ? 'Total Projected Costs'
                : 'Projected Costs'
            }
            body={(jc) =>
              jc.isAddRow ? null : (
                <span
                  className={`${
                    (jc.ActualCost > jc.ProjCost ||
                      jc.CommitCost > jc.ProjCost) &&
                    'errorText'
                  } `}
                >
                  <TooltipContent
                    id={`PC${jc.Phase.replace(/ /g, '_')}`}
                    label={formatCurrency(jc.ProjCost) || ''}
                    content={jc.Note}
                    bold={!!jc.Note}
                  />
                </span>
              )
            }
            headerClassName={`font-normal ${columnHeaderClass} justify-content-center ${
              (extraColumns?.length || 0) > 0 && 'bg-teal-300'
            }`}
            style={{
              minWidth: (extraColumns?.length || 0) > 0 ? '190px' : '150px',
            }}
            className={`text-standard blackText tableCell justify-content-end`}
            footer={formatCurrency(totProjCost)}
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            sortable
          />
          <Column
            field="NewProjCost"
            header="New Project Costs"
            body={(jc) => {
              if (jc.isAddRow) {
                return null;
              }

              if (jc.isNew && !jc.CostType) {
                return null;
              }

              if (jc.NewProjCost != null) {
                return (
                  <div
                    className={`w-full text-right ${
                      jc.ActualCost > jc.NewProjCost ||
                      jc.CommitCost > jc.NewProjCost
                        ? 'errorText'
                        : canEdit && 'totalColor'
                    } printColor cursor-pointer`}
                  >
                    {formatCurrency(jc.NewProjCost)}
                  </div>
                );
              }

              return (
                <div className="w-full h-full cursor-pointer flex justify-content-center p-2">
                  {canEdit && (
                    <i
                      className={`pi pi-pencil totalColor printHide my-auto`}
                    />
                  )}
                </div>
              );
            }}
            style={{ minWidth: '160px' }}
            headerClassName="tableHeader font-normal text-center"
            className={`justify-content-center text-standard blackText tableCell p-0`}
            editor={
              canEdit
                ? (options) => {
                    return options.rowData.isAddRow ||
                      (options.rowData.isNew && !options.rowData.CostType)
                      ? null
                      : textEditor(options);
                  }
                : null
            }
            onCellEditComplete={onCellEditComplete}
            footerClassName="tableFooter block text-right border-top-2 mt-4 p-2"
            footer={(e) => {
              const records = e.props.value;
              let total = 0;
              records.forEach((record: JCProjectedCost) => {
                total += record.NewProjCost ?? 0;
              });
              return total ? formatCurrency(total) : '';
            }}
          />
          <Column
            field="OverUnder"
            header="Over/Under"
            body={(jc) => (jc.isAddRow ? null : formatCurrency(jc.OverUnder))}
            style={{ minWidth: '160px' }}
            headerClassName="tableHeader font-normal text-center"
            className="justify-content-center text-standard blackText tableCell"
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            footer={(e) => {
              const records = e.props.value;
              let total = 0;
              records.forEach((record: JCProjectedCost) => {
                total += record.OverUnder ?? 0;
              });
              return total ? formatCurrency(total) : '';
            }}
          />
          <Column
            field="NewNote"
            header="Note"
            body={(jc) => {
              if (!canEdit || jc.isAddRow || (jc.isNew && !jc.CostType)) {
                return null;
              }

              return (
                <JCNoteButton
                  cost={jc}
                  onBlur={onNoteEditComplete}
                  defaultValue={
                    costsDraft[getProjectedCostKey(jc.Phase, jc.CostType)]?.note
                  }
                />
              );
            }}
            headerClassName="tableHeader font-normal justify-content-center text-center"
            className="justify-content-center text-standard blackText tableCell min-w-50px max-w-50px pmin-w-250px pmax-w-250px"
            footerClassName="tableFooter"
          />
          {canEdit && (
            <Column
              body={(jc, options) =>
                jc.isNew ? (
                  <React.Fragment>
                    <i
                      id={`remove-projected-phase-${options.rowIndex}`}
                      className="pi pi-trash cursor-pointer"
                      onClick={() => removeRow(jc)}
                      data-pr-position="left"
                      data-pr-tooltip="Remove phase"
                    />
                    <Tooltip
                      target={`#remove-projected-phase-${options.rowIndex}`}
                    />
                  </React.Fragment>
                ) : null
              }
              style={{ minWidth: '50px', maxWidth: '50px' }}
              headerClassName="tableHeader"
              className="justify-content-center text-standard tableCell printHide"
              footerClassName="tableFooter"
            />
          )}
        </Table>
        <div className="ml-auto w-full px-3 sm:px-0 sm:w-24rem mr-3 my-4 text-standard flex justify-content-between border-bottom-2">
          <span className="font-bold">New Projected Cost</span>
          <span id="newProjectedCost">
            {formatCurrency(totProjBase - (overUnder || 0))}
          </span>
        </div>
        {selectedCompany?.isActive && (
          <div className="mt-3 flex gap-4 mx-3 printHide">
            <JCProjectedCostProcess
              draft={{ ...draftDefault, costs: costsDraft }}
              approvalMode={approval}
              access={access}
              blockAll={isEditing}
              resetDraft={reset}
              afterApproval={afterApproval}
              validate={validateNewRows}
            />
          </div>
        )}
      </div>
    );
  }
);

const transactionsAreEqual = (
  prevTransactions: Readonly<JCProjectedCostTableProps>,
  nextTransactions: Readonly<JCProjectedCostTableProps>
) => {
  return prevTransactions.costs === nextTransactions.costs;
};

export default React.memo(JCProjectedCostTable, transactionsAreEqual);
